/**
 * DUTCH-14 — Resolve profile form defaults from app profile + Clerk.
 *
 * App DB (`/api/auth/me`) is preferred when populated. When those fields are
 * empty/null (common after Clerk-only signup or partial JIT provisioning),
 * fall back to Clerk firstName/lastName and company metadata / org name so
 * /profiel is not blank for users who already have identity data in Clerk.
 */

export function nonEmpty(...vals: Array<string | null | undefined>): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return '';
}

type ClerkMeta = Record<string, unknown> | null | undefined;

export function companyFromClerkMeta(
  unsafeMetadata: ClerkMeta,
  publicMetadata: ClerkMeta,
  organizationName?: string | null,
): string {
  const unsafe = unsafeMetadata ?? {};
  const pub = publicMetadata ?? {};
  const fromUnsafe = typeof unsafe.companyName === 'string' ? unsafe.companyName : '';
  const fromPublic = typeof pub.companyName === 'string' ? pub.companyName : '';
  return nonEmpty(fromUnsafe, fromPublic, organizationName);
}

export interface ProfileNameSources {
  /** App profile from `/api/auth/me` (DB). */
  appFirstName?: string | null;
  appLastName?: string | null;
  appCompanyName?: string | null;
  /** Clerk user core fields. */
  clerkFirstName?: string | null;
  clerkLastName?: string | null;
  clerkUnsafeMetadata?: ClerkMeta;
  clerkPublicMetadata?: ClerkMeta;
  /** Active Clerk organization name, if any. */
  organizationName?: string | null;
}

export interface ResolvedProfileFormDefaults {
  firstName: string;
  lastName: string;
  companyName: string;
}

/** Prefer app/DB values; fill gaps from Clerk identity + company metadata. */
export function resolveProfileFormDefaults(
  sources: ProfileNameSources,
): ResolvedProfileFormDefaults {
  return {
    firstName: nonEmpty(sources.appFirstName, sources.clerkFirstName),
    lastName: nonEmpty(sources.appLastName, sources.clerkLastName),
    companyName: nonEmpty(
      sources.appCompanyName,
      companyFromClerkMeta(
        sources.clerkUnsafeMetadata,
        sources.clerkPublicMetadata,
        sources.organizationName,
      ),
    ),
  };
}
